import { MoreVertical } from "lucide-react";
import { ReactComponent as StatusInfoIcon } from "../../Assets/icon/status-info-circle.svg";
import { ReactComponent as GroupBlackIcon } from "../../Assets/icon/group-black.svg";
import { ReactComponent as EditIcon } from "../../Assets/images/edit.svg";
import { ReactComponent as TrashRedIcon } from "../../Assets/icon/trash_red.svg";
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";

// Status mapping for pill text and styling
const STATUS = {
  pending: "pending",
  invited: "invited",
  accepted: "accepted",
  blocked: "blocked",
  cancelled: "cancelled",
  deleted: "deleted",
  nonExist: "nonExist",
};

const getStatusLabel = (status) => {
  if (!status) return "";
  const s = String(status).toLowerCase();
  if (s === STATUS.accepted) return "Connected";
  if (s === STATUS.pending) return "Approval Pending";
  if (s === STATUS.invited) return "Invited";
  // NonExist/Cancelled/Deleted/Blocked -> no label (blank)
  return "";
};

const ManageRelationshipContactCard = ({
  contact,
  onEdit,
  onDelete,
  onGetAdvice, // navigate to relationship advice chat for this contact
}) => {
  const [imageError, setImageError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const fullName = `${contact?.first_name ?? ""} ${contact?.last_name ?? ""}`
    .trim()
    .replace(/\s+/g, " ");
  const initials = `${contact?.first_name?.[0] ?? ""}${contact?.last_name?.[0] ?? ""
    }`
    .toUpperCase()
    .trim();
  const category =
    contact?.relationship_category ||
    contact?.related_data?.relationship_category ||
    "";

  const status = contact?.status;
  const statusLabel = getStatusLabel(status);
  const statusNorm = String(status).toLowerCase();
  const isConnected = statusNorm === STATUS.accepted;
  const tooltipId = `status-tooltip-${contact?._id || Math.random()}`;
  const tooltipText = isConnected ? "Connected" : statusLabel === "Invited" ? "Invited" : "Approval Pending";
  const statusIcon = isConnected
    ? (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15.5641 7.32024C15.417 7.34486 15.2857 7.42692 15.1991 7.54836C15.1124 7.66981 15.0776 7.82069 15.1022 7.96782C15.1593 8.30892 15.188 8.65416 15.188 9.00001C15.1893 10.5147 14.6326 11.9768 13.6242 13.107C12.9969 12.1979 12.1147 11.4946 11.0888 11.0855C11.6399 10.6514 12.042 10.0564 12.2393 9.38314C12.4366 8.70991 12.4191 7.99193 12.1894 7.32906C11.9597 6.6662 11.5292 6.09138 10.9577 5.68456C10.3861 5.27773 9.70202 5.05912 9.00048 5.05912C8.29894 5.05912 7.61483 5.27773 7.0433 5.68456C6.47177 6.09138 6.04121 6.6662 5.81151 7.32906C5.58181 7.99193 5.56438 8.70991 5.76165 9.38314C5.95892 10.0564 6.36108 10.6514 6.9122 11.0855C5.88621 11.4946 5.00408 12.1979 4.37673 13.107C3.58551 12.2154 3.06868 11.1142 2.88838 9.9359C2.70808 8.75758 2.87198 7.55226 3.36038 6.46487C3.84878 5.37748 4.64089 4.45432 5.64145 3.80638C6.64202 3.15844 7.80844 2.81332 9.00048 2.81251C9.34632 2.81245 9.69157 2.84115 10.0327 2.89829C10.1791 2.9214 10.3288 2.88574 10.4491 2.79907C10.5694 2.7124 10.6507 2.58173 10.6751 2.43548C10.6996 2.28923 10.6653 2.13924 10.5798 2.01812C10.4942 1.89701 10.3643 1.81458 10.2183 1.78876C8.69071 1.53178 7.12098 1.76692 5.73575 2.46022C4.35052 3.15353 3.22147 4.26914 2.51161 5.64596C1.80175 7.02278 1.54781 8.58958 1.78647 10.1201C2.02512 11.6507 2.74402 13.0658 3.83936 14.1611C4.9347 15.2565 6.34981 15.9754 7.88036 16.214C9.41091 16.4527 10.9777 16.1987 12.3545 15.4889C13.7313 14.779 14.847 13.65 15.5403 12.2647C16.2336 10.8795 16.4687 9.30978 16.2117 7.7822C16.1871 7.63507 16.105 7.50374 15.9836 7.41711C15.8622 7.33048 15.7113 7.29563 15.5641 7.32024ZM6.75048 8.43751C6.75048 7.9925 6.88244 7.55749 7.12967 7.18748C7.3769 6.81746 7.7283 6.52908 8.13944 6.35878C8.55057 6.18848 9.00297 6.14392 9.43943 6.23074C9.87589 6.31756 10.2768 6.53185 10.5915 6.84652C10.9061 7.16119 11.1204 7.5621 11.2072 7.99855C11.2941 8.43501 11.2495 8.88741 11.0792 9.29855C10.9089 9.70968 10.6205 10.0611 10.2505 10.3083C9.8805 10.5555 9.44548 10.6875 9.00048 10.6875C8.40374 10.6875 7.83144 10.4505 7.40949 10.0285C6.98753 9.60654 6.75048 9.03425 6.75048 8.43751ZM5.20923 13.8867C5.61615 13.2503 6.17673 12.7266 6.8393 12.3638C7.50186 12.001 8.24509 11.8109 9.00048 11.8109C9.75586 11.8109 10.4991 12.001 11.1617 12.3638C11.8242 12.7266 12.3848 13.2503 12.7917 13.8867C11.7077 14.7298 10.3737 15.1875 9.00048 15.1875C7.62726 15.1875 6.29321 14.7298 5.20923 13.8867ZM16.7109 3.21048L14.4609 5.46048C14.4087 5.51278 14.3467 5.55427 14.2784 5.58257C14.2101 5.61088 14.1369 5.62545 14.063 5.62545C13.9891 5.62545 13.9159 5.61088 13.8476 5.58257C13.7793 5.55427 13.7172 5.51278 13.665 5.46048L12.54 4.33548C12.4877 4.28321 12.4463 4.22117 12.418 4.15289C12.3897 4.0846 12.3752 4.01142 12.3752 3.93751C12.3752 3.8636 12.3897 3.79041 12.418 3.72213C12.4463 3.65385 12.4877 3.5918 12.54 3.53954C12.6456 3.43399 12.7887 3.3747 12.938 3.3747C13.0119 3.3747 13.0851 3.38925 13.1534 3.41754C13.2216 3.44582 13.2837 3.48728 13.3359 3.53954L14.063 4.26727L15.915 2.41454C15.9673 2.36228 16.0293 2.32082 16.0976 2.29254C16.1659 2.26425 16.2391 2.2497 16.313 2.2497C16.3869 2.2497 16.4601 2.26425 16.5284 2.29254C16.5966 2.32082 16.6587 2.36228 16.7109 2.41454C16.7632 2.4668 16.8047 2.52885 16.8329 2.59713C16.8612 2.66541 16.8758 2.7386 16.8758 2.81251C16.8758 2.88642 16.8612 2.9596 16.8329 3.02789C16.8047 3.09617 16.7632 3.15821 16.7109 3.21048Z" fill="#149041"/>
      </svg>
    )
    : <StatusInfoIcon className="status-icon-svg" style={{ width: 15, height: 15 }} />;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="manage-card">
      <div className="manage-card-content">
        {contact?.profile_pic_url && !imageError ? (
          <div className="manage-avatar">
            <img
              src={contact?.profile_pic_url}
              alt={fullName}
              className="manage-avatar-image"
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <div className="manage-avatar">{initials}</div>
        )}
        <div className="manage-info">
          <div className="manage-name">{fullName}</div>
          <div className="manage-type">{category}</div>
        </div>
      </div>

      <div className="manage-right">
        {statusLabel ? (
          <span
            className={`status-pill status-sharing`}
            data-tooltip-id={tooltipId}
            data-tooltip-content={tooltipText}
          >
            {statusIcon}
            <span className="status-text">{statusLabel}</span>
            <Tooltip id={tooltipId} place="top" className="status-tooltip" />
          </span>
        ) : (
          <span className="status-pill status-blank" />
        )}

        <div className="menu-wrapper" ref={menuRef}>
          <button
            className="manage-more-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="More actions"
          >
            <MoreVertical size={20} />
          </button>

          {menuOpen && (
            <div className="relationship-menu">
              {isConnected && (
                <button
                  className="relationship-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    onGetAdvice?.(contact);
                  }}
                >
                  <div className="menu-item-content">
                    <GroupBlackIcon className="menu-icon" />
                    <span>Get Relationship Advice</span>
                  </div>
                </button>
              )}
              <button
                className="relationship-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit?.(contact);
                }}
              >
                <div className="menu-item-content">
                  <EditIcon className="menu-icon" />
                  <span>Edit</span>
                </div>
              </button>
              <button
                className="relationship-menu-item danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDelete?.(contact);
                }}
              >
                <div className="menu-item-content">
                  <TrashRedIcon className="menu-icon" />
                  <span>Delete</span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageRelationshipContactCard;
